import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAgents } from "@/components/Agents/AgentsContext";
import { isMailConnection } from "@/lib/agentsDemoData";
import { agentsHref } from "@/lib/atwAgentsHost";

export function CreateSharedInboxPage() {
  const { org, may, viewOnly, addSharedInbox } = useAgents();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [connectionId, setConnectionId] = useState("");
  const [note, setNote] = useState("");
  const canCreate = may("manage_connections");

  const mailConnections = useMemo(
    () => org.connections.filter((conn) => isMailConnection(conn)),
    [org.connections],
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!canCreate) return;
    const id = addSharedInbox({
      name,
      emailAddress,
      connectionId,
      note,
    });
    if (!id) {
      toast.error(
        mailConnections.length === 0
          ? "Add a mail connection on Connections first."
          : "Enter a name, email address, and mail connection.",
      );
      return;
    }
    toast.success(`${name.trim()} added.`);
    navigate(agentsHref("/connections/email"));
  };

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={agentsHref("/connections/email")}
          className="mb-2 inline-flex items-center gap-1 text-sm text-gray-600 hover:underline dark:text-gray-400"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Group / Shared inboxes
        </Link>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Add a shared inbox
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
          Create a team address for the organization. Agents can attach to it later. This is not a
          person&apos;s personal email.
        </p>
      </div>

      {viewOnly ? (
        <p className="max-w-2xl rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
          Standard role is view only. Ask a Super Admin to add a shared inbox.
        </p>
      ) : null}

      {mailConnections.length === 0 ? (
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle className="text-base">Mail connection required</CardTitle>
            <CardDescription>
              Shared inboxes need a mail-capable connection (ATW Mail, Google, or Microsoft).
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Link
              to={agentsHref("/connections")}
              className="inline-flex h-8 items-center rounded-md border border-gray-300 px-3 text-xs font-semibold hover:bg-gray-50 dark:border-white/20"
            >
              Connections
            </Link>
            <Link
              to={agentsHref("/connections/new")}
              className="inline-flex h-8 items-center rounded-md border border-gray-300 px-3 text-xs font-semibold hover:bg-gray-50 dark:border-white/20"
            >
              Add a connection
            </Link>
          </CardContent>
        </Card>
      ) : (
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle className="text-base">New shared inbox</CardTitle>
            <CardDescription>
              Link the team address to a mail connection. Credentials are not collected here.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={submit}>
              <label className="block text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-300">Name / label</span>
                <Input
                  className="mt-1 w-full"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Support inbox"
                  disabled={!canCreate}
                  autoComplete="off"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-300">Email address</span>
                <Input
                  className="mt-1 w-full"
                  type="email"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  placeholder="support@example.com"
                  disabled={!canCreate}
                  autoComplete="off"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  Mail connection
                </span>
                <select
                  className="mt-1 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none ring-indigo-500 focus:ring-2 dark:border-white/10 dark:bg-white/5 dark:text-white"
                  value={connectionId}
                  onChange={(e) => setConnectionId(e.target.value)}
                  disabled={!canCreate}
                >
                  <option value="">Select a mail connection</option>
                  {mailConnections.map((conn) => (
                    <option key={conn.id} value={conn.id}>
                      {conn.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  Note (optional)
                </span>
                <Input
                  className="mt-1 w-full"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional note"
                  disabled={!canCreate}
                  autoComplete="off"
                />
              </label>
              <div className="flex flex-wrap gap-2">
                <Button type="submit" disabled={!canCreate}>
                  Save
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(agentsHref("/connections/email"))}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
